<template>
  <div class="legal-page">
    <header class="legal-hero">
      <div class="legal-glow" aria-hidden="true"></div>
      <div class="lp-container legal-hero__inner">
        <div class="lp-eyebrow"><span class="lp-eyebrow__dot"></span>{{ eyebrow }}</div>
        <h1 class="lp-h2 legal-title lp-balance">{{ title }}</h1>
        <p v-if="updated" class="lp-lead legal-updated">{{ updated }}</p>
      </div>
    </header>

    <section class="lp-container legal-body">
      <article v-for="(paragraph, index) in paragraphs" :key="index" class="lp-card legal-card">
        <h2 class="legal-card__title">{{ paragraph.title }}</h2>
        <template v-for="(block, bi) in blocks(paragraph.text)">
          <ul v-if="block.list" :key="bi" class="legal-list">
            <li v-for="(item, li) in block.items" :key="li">{{ item }}</li>
          </ul>
          <p v-else :key="bi" class="legal-text">{{ block.text }}</p>
        </template>
      </article>
      <slot></slot>
    </section>
  </div>
</template>

<script>
/**
 * Layout común de las páginas legales (privacidad, términos, legal, cookies)
 * con la misma estética que la home (tokens lp-* de landing.css).
 *
 * El AppBar es un header fijo de 64px y Vuetify pone padding-top:0 inline en
 * v-main, así que cada página interior debe despejarlo por su cuenta: aquí lo
 * hace el padding-top de .legal-hero.
 *
 * `paragraphs` = [{ title, text }]. En `text`, cada línea es un párrafo y las
 * líneas que empiezan por "- " se agrupan como lista con viñetas.
 */
export default {
  name: 'LegalPageLayout',
  props: {
    eyebrow: { type: String, default: 'Legal' },
    title: { type: String, required: true },
    updated: { type: String, default: '' },
    paragraphs: { type: Array, required: true },
  },
  methods: {
    blocks(text) {
      const out = [];
      for (const raw of String(text).split('\n')) {
        const line = raw.trim();
        if (!line) continue;
        if (line.startsWith('- ')) {
          const last = out[out.length - 1];
          if (last && last.list) last.items.push(line.slice(2));
          else out.push({ list: true, items: [line.slice(2)] });
        } else {
          out.push({ list: false, text: line });
        }
      }
      return out;
    },
  },
};
</script>

<style scoped>
.legal-page {
  background: var(--lp-bg);
  min-height: 100vh;
}

/* 64px del AppBar fijo + aire, mismo lenguaje visual que el hero de la home */
.legal-hero {
  position: relative;
  padding: 128px 0 24px;
  overflow: hidden;
}
.legal-glow {
  position: absolute;
  top: -160px;
  left: 50%;
  transform: translateX(-50%);
  width: 1100px;
  height: 560px;
  background: radial-gradient(60% 60% at 50% 0%, var(--lp-accent-weak) 0%, rgba(0, 0, 0, 0) 70%);
  pointer-events: none;
  z-index: 0;
}
.legal-hero__inner {
  position: relative;
  z-index: 1;
  max-width: 820px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}
.legal-title { margin: 0; }
.legal-updated { font-size: 1rem; }

.legal-body {
  max-width: 820px;
  padding-bottom: 112px;
}
.legal-card {
  padding: 26px 28px;
  margin-top: 18px;
}
.legal-card__title {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 1.2rem;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: var(--lp-ink);
  margin: 0 0 12px;
}
.legal-text {
  color: var(--lp-ink-2);
  font-size: 0.98rem;
  line-height: 1.65;
  margin: 0 0 10px;
}
.legal-text:last-child { margin-bottom: 0; }
.legal-list {
  margin: 0 0 10px;
  padding-left: 22px;
}
.legal-list li {
  color: var(--lp-ink-2);
  font-size: 0.98rem;
  line-height: 1.65;
  margin-bottom: 6px;
}
.legal-list li::marker { color: var(--lp-accent); }

@media (max-width: 599px) {
  .legal-hero { padding: 104px 0 12px; }
  .legal-card { padding: 20px 18px; }
}
</style>
